import React, { useCallback } from 'react';

import { PropTypes } from 'prop-types';

import { DropDown } from '../DropDown/DropDown';
import { ActualPlansIds, PlansIds, SUBSCRIPTION_PERIOD } from 'constants/config';
import { openChangePlanChat } from 'services/SupportChatService';
import { getFastspringSessionId, prorateSubscription } from 'services/belivePaymentService';
import { getOriginQueryParam } from 'services/queryParamsService';
import { getStartTrialUrl, getUserProfileUrl } from 'utils/links';
import { isAppsumoPlan } from 'utils/user';
import {
  maxDropDownHeight,
  StyledDescription,
  StyledDropDownArrow,
  StyledOptionsWrapper,
  StyledPlan,
  StyledPlanBody,
  StyledPlanButton,
  StyledPlanName,
  StyledPricesWrapper,
  StyledSpecialHeader,
} from 'components/Plan/PlanStyles';
import { getChangePlanMessage } from 'components/Plan/getChangePlanMessage';
import {
  getButtonText,
  getPlanOptionsListByType,
  getPriceWithDiscount,
  isDowngrade,
} from 'components/Plan/planUtils';
import { PriceComponent } from './PriceComponent';
import { PopupModeService } from 'services/popupModeService';
import { BilledAnnually } from 'components/BilledAnnually';
import { IframeModeService } from 'services/iframeModeService';
import { navigateTo } from 'utils/navigateTo';

const PROCEED_CHECKOUT_ACTIONS = {
  REDIRECT_TO_PROFILE: 'redirect_to_profile',
  OPEN_FASTSPRING_STORE_POPUP: 'open_fastspring_store_popup',
};

export const Plan = React.memo((props) => {
  const {
    name,
    isSelected,
    isDisabled,
    isAppSumo,
    isAnnuallyPlan,
    yearlyMonthlyPrice,
    monthlyPrice,
    lifetimePrice,
    description,
    optionsList,
    isCompactMode,
    isCurrentPlan,
    isCurrentInterval,
    isAuthorized,
    isTrial,
    planId,
    isExpanded,
    planPaymentPath,
    choosePlan,
    userEmail,
    userId,
    currencySymbol,
    currentUserPlan,
    currentPlanInterval,
    promo,
    showLoader,
    openConfirmationPopup,
    buttonLink,
    withoutDiscounts,
    mobileOnly,
    special,
  } = props;

  const userIsOnFreePlan = currentUserPlan === PlansIds.FREE;
  const isLifetime = yearlyMonthlyPrice < 0 && monthlyPrice < 0 && lifetimePrice >= 0;
  const calculatedButtonText = getButtonText({ ...props, isLifetime });
  const subscriptionPrice = isAnnuallyPlan ? yearlyMonthlyPrice : monthlyPrice;
  const currentPrice = isLifetime ? lifetimePrice : subscriptionPrice;
  const isFree = planId === PlansIds.FREE;
  const isButtonDisabled =
    isDisabled ||
    (isAppSumo && isFree) ||
    (isCurrentPlan && (isCurrentInterval || userIsOnFreePlan) && !isTrial && !isAppSumo);

  const dropDownContent = Object.entries(optionsList).reduce(
    (acc, [type, options]) => acc.concat(getPlanOptionsListByType(type, options)),
    [],
  );

  const upgradeUser = async () => {
    if (window.fastspring && planPaymentPath) {
      let checkout = true;
      const origin = getOriginQueryParam();
      try {
        checkout = await getFastspringSessionId({
          plan: planPaymentPath,
          coupon: promo.coupon,
          origin,
        }); // sessionId uses for calculating price with credits
      } catch (error) {
        console.error(error);
      }

      window.fastspring.builder.push({
        paymentContact: {
          email: userEmail,
        },
        products: [{ path: planPaymentPath }],
        checkout,
        coupon: promo.coupon,
        tags: { user_id: userId, origin },
      });
    } else {
      await choosePlan(planId);
    }
  };

  const openCheckoutChat = () => {
    const price = getPriceWithDiscount(currentPrice, promo.discount | 0);
    const priceText = `${currencySymbol}${price}`;
    const text = getChangePlanMessage(
      { plan: currentUserPlan, interval: currentPlanInterval },
      {
        plan: planId,
        interval: isAnnuallyPlan ? SUBSCRIPTION_PERIOD.YEARLY : SUBSCRIPTION_PERIOD.MONTHLY,
      },
      priceText,
    );

    if (PopupModeService.isPopupMode()) {
      PopupModeService.openSupportChat(text);
    } if (IframeModeService.isIframeMode()) {
      IframeModeService.openSupportChat(text);
    } else {
      openChangePlanChat(text);
    }
  };

  const proceedCheckoutByAction = async (action) => {
    if (action === PROCEED_CHECKOUT_ACTIONS.REDIRECT_TO_PROFILE) {
      // for fastspring user
      if (PopupModeService.isPopupMode()) {
        PopupModeService.upgradeComplete();
      } else {
        navigateTo(getUserProfileUrl(userId));
      }
      return;
    }

    if (action === PROCEED_CHECKOUT_ACTIONS.OPEN_FASTSPRING_STORE_POPUP) {
      // for paypal user, backend will create a new subscription on fastspring and refund costs from paypal
      await upgradeUser();
      return;
    }

    openCheckoutChat();
  };

  const isDowngradingPlan = () => {
    // if user want switch from yearly to monthly => always downgrade
    const isIntervalDowngrade =
      currentPlanInterval === SUBSCRIPTION_PERIOD.YEARLY && !isAnnuallyPlan;
    const isIntervalUpgrade = currentPlanInterval === SUBSCRIPTION_PERIOD.MONTHLY && isAnnuallyPlan;
    const isPlanDowngrade = isDowngrade(currentUserPlan, planId) && !isIntervalUpgrade; // plan is downgraded if plan is smaller and interval is not upgraded

    return isIntervalDowngrade || (isPlanDowngrade && !isIntervalUpgrade);
  };

  const prorateUserSubscription = async () => {
    try {
      // show loader, because this action can take some time
      showLoader(true);
      const { action } = await prorateSubscription({
        plan: planPaymentPath,
        coupon: promo.coupon,
        origin: getOriginQueryParam(),
      });
      await proceedCheckoutByAction(action);
    } catch (error) {
      console.error(error);
    } finally {
      showLoader(false);
    }
  };

  const onUpgradeButtonClick = useCallback(async () => {
    if (buttonLink) {
      navigateTo(buttonLink);
      return;
    }

    if (isAuthorized) {
      if (isFree) {
        // if user isAppSumo or select free plan => open chat
        openCheckoutChat();
        return;
      }
      // if user is on free plan or trial and select not free plan -> just upgrade
      if (userIsOnFreePlan || isTrial || currentUserPlan === ActualPlansIds.ZERO) {
        await upgradeUser();
      } else if (!isAppsumoPlan(currentUserPlan) && isDowngradingPlan()) {
        // if user want to downgrade plan or interval and current plan is not appsumo plan -> open chat
        openCheckoutChat();
      } else {
        // if user has subscription and not trial => user can upgrade after confirmation
        openConfirmationPopup({ confirmAction: prorateUserSubscription });
      }
    } else {
      // authorize link with selected planId
      navigateTo(getStartTrialUrl(planId));
    }
  }, [planId, isAnnuallyPlan, isAuthorized, planPaymentPath, promo]);

  const isDiscountVisible = !isFree && !withoutDiscounts && Boolean(promo.discount);
  const discountPrice = getPriceWithDiscount(currentPrice, promo.discount);
  const finalPrice = isDiscountVisible ? discountPrice : currentPrice;
  const isBilledAnnuallyVisible = isAnnuallyPlan && !lifetimePrice;

  return (
    <StyledPlan isSelected={isSelected} mobileOnly={mobileOnly} special={special}>
      <StyledSpecialHeader>{special}</StyledSpecialHeader>
      <StyledPlanBody>
        <StyledPlanName>{name}</StyledPlanName>
        <StyledPricesWrapper>
          <PriceComponent
            isDisabled={isDisabled}
            isFree={isFree}
            price={finalPrice}
            currencySymbol={currencySymbol}
            isMonthlyPrice={!isLifetime}
          />
          {isDiscountVisible && (
            <PriceComponent
              crossedPrice={isDiscountVisible}
              isDisabled={isDisabled}
              isFree={isFree}
              price={isDiscountVisible ? currentPrice : discountPrice} // calculate price with discount
              currencySymbol={currencySymbol}
              isMonthlyPrice={!isLifetime}
            />
          )}
          <BilledAnnually
            isVisible={isBilledAnnuallyVisible}
            currency={currencySymbol}
            price={getPriceWithDiscount(yearlyMonthlyPrice, promo.discount) * 12}
          />
        </StyledPricesWrapper>
        {currentUserPlan < planId && promo.planLabel}
        <StyledPlanButton
          isSelected={isSelected && currentUserPlan !== PlansIds.PRO}
          isDisabled={isButtonDisabled}
          onClick={onUpgradeButtonClick}
        >
          {calculatedButtonText}
        </StyledPlanButton>
        {!isCompactMode && (
          <StyledOptionsWrapper>
            <DropDown
              height={maxDropDownHeight}
              content={dropDownContent}
              isDefaultExpanded={isExpanded}
            >
              {(isDropDownExpanded, toggle) => (
                <StyledDescription>
                  {description}
                  <StyledDropDownArrow onClick={toggle} isExpanded={isDropDownExpanded} />
                </StyledDescription>
              )}
            </DropDown>
          </StyledOptionsWrapper>
        )}
      </StyledPlanBody>
    </StyledPlan>
  );
});

Plan.propTypes = {
  name: PropTypes.string.isRequired,
  monthlyPrice: PropTypes.number,
  yearlyMonthlyPrice: PropTypes.number,
  planId: PropTypes.number,
  description: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  optionsList: PropTypes.object.isRequired,
  isAppSumo: PropTypes.bool.isRequired,
  isAnnuallyPlan: PropTypes.bool.isRequired,
  buttonText: PropTypes.string.isRequired,
  isCurrentPlan: PropTypes.bool,
  isAuthorized: PropTypes.bool,
  isTrial: PropTypes.bool,
  isExpanded: PropTypes.bool,
  isCompactMode: PropTypes.bool,
  isDisabled: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  planPaymentPath: PropTypes.string,
  choosePlan: PropTypes.func.isRequired,
  promo: PropTypes.object,
  currentPlanInterval: PropTypes.string,
  showLoader: PropTypes.func.isRequired,
};

Plan.defaultProps = {
  isDisabled: false,
  isCurrentPlan: false,
  isAuthorized: false,
  isTrial: false,
  isExpanded: false,
  isCompactMode: false,
  currentPlanInterval: null,
  monthlyPrice: -1,
  yearlyMonthlyPrice: -1,
  promo: {
    coupon: '',
    discount: 0,
  },
};
